import { useMemo } from 'react';
import { useSearchParams } from 'react-router';

import { curatorErrorCopy, parseCuratorVenues, useCuratorVenues } from '../../api/curator';
import { EmptyState, ErrorState, Spinner } from '../../components/ui';
import { parseVenueSelection, type VenueSelection } from './cabinet-model';
import { GroupsBlock } from './GroupsBlock';
import { VenuePicker } from './VenuePicker';
import './cabinet.css';

/**
 * «Группы» — the study groups of the cabinet scope, on their OWN page (T.Z60, owner
 * 2026-09-15). The section spent v1.9.5 (T.Z50/T.Z51) inside the cabinet; the owner's
 * acceptance pass moved it out: the management and the listing live HERE now, the
 * cabinet no longer hosts them, and the sidebar offers the page to a КУРАТОР-role
 * session right below «Педагоги» (`canSeeGroups` + `curatorNavOrder`).
 *
 * The surface itself is unchanged — a group belongs to a ПЛОЩАДКА, so the page is
 * venue-scoped exactly like the block it hosts: the same picker («Все места» cannot
 * guess where a create lands, so it answers the same pointer the cabinet used to),
 * the same block, the same testids (`cabinet-groups`, `group-*`). The selection lives
 * in THIS page's `?venueId=` param, absent = the first venue of the scope.
 */
export function GroupsPage() {
  const [params, setParams] = useSearchParams();
  const venuesQuery = useCuratorVenues();
  const venues = useMemo(() => parseCuratorVenues(venuesQuery.data), [venuesQuery.data]);

  const selection: VenueSelection | null = useMemo(
    () => parseVenueSelection(venues, params.get('venueId')),
    [venues, params],
  );
  const venue =
    selection !== null && selection.kind === 'venue'
      ? (venues.find((candidate) => candidate.venueId === selection.venueId) ?? null)
      : null;

  const setSelection = (raw: string) => {
    const next = new URLSearchParams(params);
    // An empty value (the picker's ×) lands on the page's own default: the first venue.
    const first = venues[0]?.venueId;
    const value = raw === '' ? (first === undefined ? null : String(first)) : raw;
    if (value === null) next.delete('venueId');
    else next.set('venueId', value);
    setParams(next, { replace: true });
  };

  if (venuesQuery.isPending) {
    return (
      <div className="page-header">
        <h1>Группы</h1>
        <Spinner label="Загружаем ваши площадки" />
      </div>
    );
  }

  if (venuesQuery.isError) {
    return (
      <>
        <div className="page-header">
          <h1>Группы</h1>
        </div>
        <div className="card">
          <ErrorState message={curatorErrorCopy(venuesQuery.error)} onRetry={() => void venuesQuery.refetch()} />
        </div>
      </>
    );
  }

  if (venues.length === 0) {
    return (
      <>
        <div className="page-header">
          <h1>Группы</h1>
        </div>
        <div className="card">
          <EmptyState
            title="К вам пока не привязана площадка"
            hint="Группы ведутся на закреплённых за вами площадках. Обратитесь к администратору, чтобы привязать площадку."
          />
        </div>
      </>
    );
  }

  if (selection === null) return null;

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Группы</h1>
          <p className="page-header__sub" data-testid="groups-header-sub">
            Учебные группы ваших площадок: состав до 30 учеников, педагог — из той же площадки.
          </p>
        </div>
      </div>

      <div className="cabinet-toolbar">
        <VenuePicker venues={venues} selection={selection} onChange={setSelection} />
      </div>

      {selection.kind === 'venue' && venue !== null ? (
        <GroupsBlock venueId={venue.venueId} venueName={venue.name} />
      ) : (
        <p className="card cabinet-section-hint" data-testid="cabinet-section-hint">
          Группы ведутся по одной площадке — выберите её в поле выше, чтобы посмотреть состав и педагога.
        </p>
      )}
    </>
  );
}
